import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Section, SectionHeader, fadeUp } from "../components/sections";
import LeadMagnetSection from "../components/LeadMagnetSection";
import SeoHead from "../components/SeoHead";
import { SERVICES, INDUSTRIES, PROCESS, STATS, TESTIMONIALS, CLIENT_LOGOS, TECH_STACK } from "../lib/site";
import { useEffect, useState } from "react";
import { apiClient } from "../lib/site";

export default function HomePage() {
  const [posts, setPosts] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    apiClient.get("/blog/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    apiClient.get("/case-studies").then((r) => setCases(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SeoHead
        title={null}
        description="Elite Solutions Hub — a London-based software consultancy. We architect, build, and operate mission-critical platforms for FinTech, Healthcare, E-commerce, Logistics, Education, SaaS and Government."
        path="/"
      />
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100vh] flex items-end px-6 md:px-12 lg:px-16 pb-16 md:pb-24 pt-32 overflow-hidden"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1762715461103-cf7f03090c93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjB0ZWNobm9sb2d5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NzMxMTE4MHww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/40"></div>
          <div className="absolute inset-0 grid-bg opacity-50"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl">
          <motion.p {...fadeUp} className="overline mb-8">
            Elite Solutions Hub — Software for Serious Businesses
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-serif-display font-light text-white text-balance leading-[0.98] tracking-tighter"
            style={{ fontSize: "clamp(48px, 9vw, 144px)", letterSpacing: "-0.035em" }}
          >
            Engineered <em className="italic" style={{ color: "#C2533B" }}>conviction.</em>
            <br />
            Delivered software.
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.18 }}
            className="mt-10 text-zinc-300 text-lg md:text-xl max-w-2xl leading-relaxed"
          >
            We architect, build, and operate mission-critical platforms for FinTech, Healthcare,
            E-commerce, Logistics, Education, SaaS and Government. Boring on purpose. Distinctive
            by outcome.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.26 }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
              Start a Project <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
            <Link to="/case-studies" data-testid="hero-cta-secondary" className="btn-secondary">
              See Our Work
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CLIENT LOGOS / MARQUEE */}
      <section
        data-testid="client-marquee"
        className="border-y border-white/5 py-10 overflow-hidden bg-[#0A0A0C]"
      >
        <p className="overline text-center mb-8" style={{ color: "#71717A" }}>
          Trusted by enterprise teams worldwide
        </p>
        <div className="relative">
          <div className="flex marquee-track gap-16 whitespace-nowrap">
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((name, i) => (
              <span
                key={i}
                className="font-serif-display text-2xl md:text-3xl text-zinc-500 italic"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO MANIFESTO */}
      <Section testId="manifesto-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline">A Software Partner</p>
          </div>
          <div className="md:col-span-8">
            <motion.h2
              {...fadeUp}
              className="section-title text-4xl md:text-5xl lg:text-6xl text-white text-balance"
            >
              We are the consultancy clients call when the platform <em className="italic" style={{ color: "#C2533B" }}>has to ship</em>—
              and the platform <em className="italic">has to last</em>.
            </motion.h2>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="mt-10 text-zinc-400 text-lg leading-relaxed"
            >
              For more than a decade we have built the systems that auditors approve, that traders
              trust at 9:30 a.m., that nurses reach for in a crisis, and that citizens finish
              instead of abandon. We don't write strategy decks. We deliver software, on time, with
              the runbook your team actually wanted.
            </motion.p>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.18 }}
              className="mt-10"
            >
              <Link to="/about" className="inline-flex items-center gap-2 text-white text-sm tracking-wide group">
                Read our point of view{" "}
                <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition" />
              </Link>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* STATS */}
      <section
        data-testid="stats-section"
        className="border-y border-white/5 px-6 md:px-12 lg:px-16 py-20 bg-[#0A0A0C]"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="border-l border-white/10 pl-6"
              data-testid={`stat-${i}`}
            >
              <p
                className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-light text-white"
                style={{ letterSpacing: "-0.03em" }}
              >
                {s.number}
                <span style={{ color: "#C2533B" }}>{s.suffix}</span>
              </p>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <Section testId="services-preview-section" className="bg-[#050505]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <SectionHeader
            overline="What we build"
            title={<>Eight disciplines, one delivery culture.</>}
          />
          <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-white text-sm tracking-wide group">
            All services <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A]">
          {SERVICES.slice(0, 6).map((s, i) => (
            <motion.div
              key={s.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.05 }}
              className="bg-[#050505] p-10 hover:bg-[#0A0A0C] transition-colors group"
              data-testid={`service-card-${s.id}`}
            >
              <p className="font-serif-display italic text-sm text-zinc-500 mb-6">
                {String(i + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}
              </p>
              <h3 className="font-serif-display text-2xl md:text-3xl text-white font-light mb-4">
                {s.title}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{s.summary}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* INDUSTRIES STRIP */}
      <Section testId="industries-section" className="bg-[#0A0A0C] border-y border-white/5">
        <SectionHeader
          overline="Industries we serve"
          title={<>Domain depth across seven verticals.</>}
          intro="We don't sell horizontal templates. Every engagement is staffed by people who know your industry's vocabulary, regulations, and customers."
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.04 }}
              className="card-edge p-8 group cursor-default"
              data-testid={`industry-card-${ind.id}`}
            >
              <p className="font-serif-display italic text-sm text-zinc-500 mb-4">{ind.name}</p>
              <h3 className="font-serif-display text-xl md:text-2xl text-white font-light leading-snug mb-3">
                {ind.headline}
              </h3>
              <p className="text-zinc-500 text-sm leading-relaxed">{ind.description}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/industries" className="btn-secondary" data-testid="industries-cta">
            Explore Industries <ArrowUpRight size={14} strokeWidth={1.8} />
          </Link>
        </div>
      </Section>

      {/* CASE STUDIES */}
      <Section testId="case-preview-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <SectionHeader
            overline="Selected work"
            title={<>Outcomes that keep their nerve under audit.</>}
          />
          <Link to="/case-studies" className="hidden md:inline-flex items-center gap-2 text-white text-sm tracking-wide group">
            All case studies <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cases.map((c, i) => (
            <motion.div
              key={c.slug}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              data-testid={`case-card-${c.slug}`}
            >
              <Link to={`/case-studies/${c.slug}`} className="group block">
                <div className="aspect-[4/5] overflow-hidden mb-6 bg-[#0A0A0C]">
                  <img
                    src={c.cover_image}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90"
                  />
                </div>
                <p className="overline mb-3" style={{ color: "#71717A" }}>{c.industry} · {c.duration}</p>
                <h3 className="font-serif-display text-2xl md:text-3xl text-white font-light leading-snug group-hover:text-zinc-200 transition">
                  {c.title}
                </h3>
                <p className="mt-4 text-zinc-500 text-sm leading-relaxed">{c.summary}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PROCESS */}
      <Section testId="process-section" className="bg-[#0A0A0C] border-y border-white/5">
        <SectionHeader
          overline="How we deliver"
          title={<>A delivery cadence engineered for boards and engineers in equal measure.</>}
        />
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A]">
          {PROCESS.map((p, i) => (
            <motion.div
              key={p.n}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="bg-[#0A0A0C] p-10"
              data-testid={`process-step-${p.n}`}
            >
              <p
                className="font-serif-display italic text-5xl font-light"
                style={{ color: "#C2533B" }}
              >
                {p.n}
              </p>
              <h3 className="mt-6 font-serif-display text-2xl text-white font-light">{p.title}</h3>
              <p className="mt-4 text-zinc-500 text-sm leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* TECH STACK */}
      <Section testId="tech-stack-section">
        <SectionHeader
          overline="Tools we trust"
          title={<>A pragmatic stack, not a fashion statement.</>}
          intro="We pick technology to match your operating reality, not our last engagement. These are the tools we ship with—again and again."
        />
        <div className="mt-16 flex flex-wrap gap-3">
          {TECH_STACK.map((t, i) => (
            <motion.span
              key={t}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.02 }}
              className="px-5 py-2 border border-[#27272A] text-zinc-400 text-sm hover:border-[#3F3F46] hover:text-white transition cursor-default"
            >
              {t}
            </motion.span>
          ))}
        </div>
      </Section>

      {/* LEAD MAGNET */}
      <LeadMagnetSection />

      {/* TESTIMONIALS */}
      <Section testId="testimonials-section" className="bg-[#0A0A0C] border-y border-white/5">
        <SectionHeader overline="In their words" title="What clients say after the launch." />
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#27272A]">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="bg-[#0A0A0C] p-10 flex flex-col"
              data-testid={`testimonial-${i}`}
            >
              <p
                className="font-serif-display italic text-2xl md:text-3xl text-white font-light leading-snug flex-1"
                style={{ letterSpacing: "-0.01em" }}
              >
                "{t.quote}"
              </p>
              <div className="mt-8 pt-6 border-t border-white/5">
                <p className="text-white text-sm font-medium">{t.author}</p>
                <p className="text-zinc-500 text-xs mt-1">
                  {t.role} · {t.company}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* INSIGHTS */}
      <Section testId="insights-preview-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <SectionHeader overline="Insights" title="Field notes from the platforms we ship." />
          <Link to="/insights" className="hidden md:inline-flex items-center gap-2 text-white text-sm tracking-wide group">
            All insights <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#27272A]">
          {posts.map((p, i) => (
            <motion.div
              key={p.slug}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="bg-[#050505]"
              data-testid={`insight-card-${p.slug}`}
            >
              <Link to={`/insights/${p.slug}`} className="block p-10 hover:bg-[#0A0A0C] transition-colors h-full">
                <p className="overline mb-4">{p.category} · {p.read_time}</p>
                <h3 className="font-serif-display text-2xl text-white font-light leading-snug">
                  {p.title}
                </h3>
                <p className="mt-4 text-zinc-500 text-sm leading-relaxed">{p.excerpt}</p>
                <p className="mt-8 text-zinc-600 text-xs">{p.date}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CTA BAND */}
      <section
        data-testid="cta-band"
        className="relative px-6 md:px-12 lg:px-16 py-32 md:py-40 border-t border-white/5"
      >
        <div className="absolute inset-0 -z-10 grid-bg opacity-30"></div>
        <div className="max-w-5xl">
          <p className="overline mb-8">Begin</p>
          <h2
            className="font-serif-display font-light text-white leading-[0.98] tracking-tighter"
            style={{ fontSize: "clamp(40px, 7vw, 96px)", letterSpacing: "-0.03em" }}
          >
            Bring us the platform <em className="italic" style={{ color: "#C2533B" }}>nobody else</em> wants
            to take on.
          </h2>
          <p className="mt-8 text-zinc-400 text-lg max-w-2xl leading-relaxed">
            One conversation, one architect, one honest read on what it would actually take. Free.
            No proposal templates. No filler decks.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-primary" data-testid="cta-band-primary">
              Start the Conversation <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
            <a href="mailto:info@elitesolutionshub.com" className="btn-secondary" data-testid="cta-band-email">
              info@elitesolutionshub.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
